import { useTranslation } from 'react-i18next';

export interface Chat {
  id: string;
  name: string;
  chat_type: 'private' | 'group';
  health: 'important' | 'active' | 'noisy' | 'quiet';
  signal_count?: number;
  priority?: 'high' | 'normal' | 'low' | 'muted' | 'excluded';
  last_message_at?: string;
  last_message_preview?: string;
  is_monitored?: boolean;
}

interface ChatListItemProps {
  chat: Chat;
  isActive: boolean;
  onClick: (id: string) => void;
}

const healthDotColors: Record<string, string> = {
  important: 'bg-green-500',
  active: 'bg-blue-500',
  noisy: 'bg-yellow-500',
  quiet: 'bg-gray-400',
};

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) {
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function ChatListItem({
  chat,
  isActive,
  onClick,
}: ChatListItemProps) {
  const { t } = useTranslation();

  return (
    <button
      onClick={() => onClick(chat.id)}
      className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
        isActive
          ? 'bg-primary-50 border border-primary-200'
          : 'hover:bg-gray-100 border border-transparent'
      }`}
    >
      {/* Chat type icon */}
      <span className="text-lg mt-0.5 shrink-0">
        {chat.chat_type === 'private' ? '💬' : '👥'}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Name + time */}
        <div className="flex items-center justify-between gap-2">
          <span
            className={`text-sm truncate ${
              chat.health === 'important'
                ? 'font-bold text-gray-900'
                : 'font-medium text-gray-800'
            }`}
          >
            {chat.name}
          </span>
          {chat.last_message_at && (
            <span className="text-[11px] text-gray-400 shrink-0">
              {formatTime(chat.last_message_at)}
            </span>
          )}
        </div>

        {/* Preview */}
        {chat.last_message_preview && (
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {chat.last_message_preview}
          </p>
        )}

        {/* Badges row */}
        <div className="flex items-center gap-2 mt-1">
          {/* Health dot */}
          <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${
                healthDotColors[chat.health] ?? 'bg-gray-400'
              }`}
            />
            {t(`conversations.health.${chat.health}`)}
          </span>

          {/* Signal count */}
          {chat.signal_count != null && chat.signal_count > 0 && (
            <span className="inline-flex items-center rounded-full bg-primary-100 text-primary-700 px-1.5 py-0 text-[10px] font-medium">
              {chat.signal_count}
            </span>
          )}

          {/* Priority badge */}
          {chat.priority && chat.priority !== 'normal' && (
            <span className="text-[10px] font-medium text-gray-400">
              {t(`conversations.priority.${chat.priority}`)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
